import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ARTICLES, SONGS, MARQUEE_ITEMS, SWATCHES, BRANDS, SOCIALS, IMAGES } from "../data/content";

const BASE = process.env.REACT_APP_BACKEND_URL;

export async function hubApi(path, { method = "GET", body } = {}) {
  const res = await fetch(`${BASE}/api/hub${path}`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = res.status === 204 ? {} : await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Something went wrong.");
  return data;
}

// Fallbacks mirror the bundled content so the site renders even before the
// first backend response (or if the API is unreachable).
export const HUB_DEFAULTS = {
  sections: {
    hero: {
      eyebrow: "A creative ecosystem — Issue Nº 001",
      tagline: "The Hub of It All.",
      hand_note: "one hub. many worlds.",
      subtext: "This is the central place to discover everything being built, created, explored and connected through Eclectically Nelle — beauty, music, ideas, media, projects, businesses and life. Every world branches from here.",
      sticker_1: "ideas grow here ✳",
      sticker_2: "still becoming ↺",
      image: IMAGES.hero,
      image_alt: "Editorial portrait — color and ideas blooming from one source",
      cta_primary: "Explore the hub",
      cta_secondary: "Meet Nelle",
    },
    marquee: { items: MARQUEE_ITEMS },
    about: {
      eyebrow: "About — 004 · the person is the point",
      title_a: "Many dimensions.",
      title_b: "One hub.",
      para1: "Nelle is a beauty-founder-in-the-making, a songwriter, an essayist, a conversationalist and a collector of ideas that refuse to stay in one lane. Eclectically Nelle is where all of those dimensions intersect — the creative foundation and the gateway from which much larger ventures grow.",
      para2: "Not everything here is a business. But everything here is built like it could become one.",
      dimensions: ["Beauty + Style", "Music", "Writing", "Media", "Business", "Faith", "Life"],
      quote: "I'm not building one thing. I'm building the place everything comes from.",
      cite: "— nelle, probably at 2am",
      portrait: IMAGES.portrait1,
      portrait_alt: "Nelle — editorial portrait in full color",
      portrait_caption: "nelle, mid-idea. as usual.",
      hand_note: "the woman behind\nthe hub ↓",
    },
    beauty: {
      eyebrow: "Personal expression — 006 · this is nelle's mirror, not a storefront",
      note: "experimental, fashion-forward, occasionally impractical. on purpose.",
      swatches: SWATCHES,
      captions: ["fit check: jersey + pleats + cargos", "color is a decision", "after-hours color study"],
      palette_label: "The palette so far",
      lookbook_toast: "The lookbook is being styled.",
      lookbook_toast_detail: "Visual diaries, looks and moodboards arrive with Issue Nº 002.",
    },
    music: {
      eyebrow: "The record archive — 005 · original works, pressed in color",
      note: "every song is an artifact. handle with volume.",
      player_caption: "audio drops with the releases. imagine it loudly.",
      tracklist_note: "Original songs, lyrics and the process behind them — kept like records.",
    },
    brands: { eyebrow: "The brand ecosystem — 002 · born here, not merged here", note: "each brand gets its own world. nelle keeps the keys to the building.", brands: BRANDS },
    ideas_head: {
      eyebrow: "The evolving magazine — 007 · filed loosely, on purpose",
      title_a: "Culture",
      title_b: "Ideas",
      note: "things nelle thinks about. new issues when the thinking piles up.",
      footer_note: "reading time is honest. opinions are hers.",
    },
    poetry_head: {
      eyebrow: "The quiet pages — poems in progress and finished",
      title_a: "Poetry",
      title_b: "& margins",
      note: "soft things, sharp things, true things.",
      footer_note: "written between everything else.",
    },
    faith: {
      eyebrow: "Foundation — 008",
      quote: "Whatever is true, whatever is noble, whatever is lovely, whatever is admirable — think about such things.",
      cite: "— philippians 4:8, taped above the desk",
      body: "Faith isn't a department of the brand. It's the ground under it — in the music, the words, the work. Quiet, and everywhere.",
    },
    footer: {
      eyebrow: "The last page — turn it slowly",
      dispatch_title: "The Dispatch",
      dispatch_blurb: "An occasional letter from the hub. New worlds, new work, first dibs.",
      copyright: "© 2026 Eclectically Nelle",
      hand_note: "issue nº 001 — grown on the internet",
      socials: SOCIALS,
    },
  },
  collections: { ideas: ARTICLES, poems: [], songs: SONGS },
};

const Ctx = createContext(null);

export function HubContentProvider({ children }) {
  const [sections, setSections] = useState({});
  const [collections, setCollections] = useState({ ideas: null, poems: null, songs: null });
  const [editMode, setEditMode] = useState(false);
  const [editor, setEditor] = useState(null); // {type:'section',key} | {type:'item',kind,item|null}

  useEffect(() => {
    hubApi("/content")
      .then((d) => {
        setSections(d.sections || {});
        setCollections({ ideas: d.ideas, poems: d.poems, songs: d.songs });
      })
      .catch(() => {});
  }, []);

  const value = useMemo(
    () => ({
      section: (key) => ({ ...(HUB_DEFAULTS.sections[key] || {}), ...(sections[key] || {}) }),
      collection: (kind) => collections[kind] ?? HUB_DEFAULTS.collections[kind],
      editMode,
      setEditMode,
      editor,
      setEditor,
      saveSection: async (key, data) => {
        await hubApi(`/content/${key}`, { method: "PUT", body: { data } });
        setSections((s) => ({ ...s, [key]: data }));
      },
      resetSection: async (key) => {
        const r = await hubApi(`/content/${key}/reset`, { method: "POST" });
        setSections((s) => ({ ...s, [key]: r.data }));
        return r.data;
      },
      addItem: async (kind, item) => {
        const r = await hubApi(`/${kind}`, { method: "POST", body: item });
        setCollections((c) => ({ ...c, [kind]: [...(c[kind] || []), r.item] }));
        return r.item;
      },
      updateItem: async (kind, id, item) => {
        await hubApi(`/${kind}/${id}`, { method: "PUT", body: item });
        setCollections((c) => ({ ...c, [kind]: c[kind].map((x) => (x.id === id ? { ...x, ...item } : x)) }));
      },
      removeItem: async (kind, id) => {
        await hubApi(`/${kind}/${id}`, { method: "DELETE" });
        setCollections((c) => ({ ...c, [kind]: c[kind].filter((x) => x.id !== id) }));
      },
    }),
    [sections, collections, editMode, editor]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useHub = () => useContext(Ctx);
